import './manage.css';
import { apiRequest } from './api.js';
import { combineCacheLists, parseGpx, SAMPLE_CACHES } from './gpx.js';

const STORAGE_KEY = 'cacheline_admin_v1';
const PIN_STORAGE_KEY = 'cacheline_pin_v1';
const state = {
  token: localStorage.getItem(STORAGE_KEY) || '',
  pin: localStorage.getItem(PIN_STORAGE_KEY) || '',
  role: '',
  libraryId: '',
  caches: [],
  pendingCaches: [],
  pendingName: '',
  noticeTimer: null,
};

const elements = Object.fromEntries([
  'welcome', 'dashboard', 'create-library', 'pin-form', 'library-pin-input', 'open-library',
  'library-summary', 'sync-list', 'switch-library', 'gpx-file',
  'import-preview', 'preview-count', 'preview-name', 'import-button', 'sample-button',
  'pair-placeholder', 'pair-code', 'pair-expiry', 'pair-button', 'cache-list',
  'clear-button', 'recovery-panel', 'copy-key', 'forget-library', 'delete-library', 'notice',
].map((id) => [id, document.getElementById(id)]));

elements['create-library'].addEventListener('click', createLibrary);
elements['pin-form'].addEventListener('submit', openLibraryByPin);
elements['sync-list'].addEventListener('click', () => loadCaches());
elements['switch-library'].addEventListener('click', switchLibrary);
elements['gpx-file'].addEventListener('change', handleFiles);
elements['import-button'].addEventListener('click', importPending);
elements['sample-button'].addEventListener('click', useSampleCaches);
elements['pair-button'].addEventListener('click', createPairing);
elements['clear-button'].addEventListener('click', clearCaches);
elements['copy-key'].addEventListener('click', copyRecoveryKey);
elements['forget-library'].addEventListener('click', forgetLibrary);
elements['delete-library'].addEventListener('click', deleteLibrary);

boot();

async function boot() {
  if (!state.token) return showWelcome();
  state.libraryId = state.token.split('.')[1] || '';
  state.role = state.token.split('.')[0] || '';
  showDashboard();
  await loadCaches({ quiet: true });
}

function showWelcome() {
  elements.welcome.classList.remove('hidden');
  elements.dashboard.classList.add('hidden');
  requestAnimationFrame(() => elements['library-pin-input'].focus());
}

function showDashboard() {
  elements.welcome.classList.add('hidden');
  elements.dashboard.classList.remove('hidden');
  renderAccessControls();
  renderPin();
}

async function createLibrary() {
  setBusy(elements['create-library'], true, 'Creating…');
  try {
    const result = await apiRequest('libraries', { body: {} });
    state.token = result.adminToken;
    state.pin = result.pin;
    state.role = 'admin';
    state.libraryId = result.libraryId;
    localStorage.setItem(STORAGE_KEY, state.token);
    localStorage.setItem(PIN_STORAGE_KEY, state.pin);
    showDashboard();
    await loadCaches({ quiet: true });
    notice(`Private library created. Your permanent PIN is ${formatPin(state.pin)}.`);
  } catch (error) {
    notice(error.message || 'The library could not be created.');
  } finally {
    setBusy(elements['create-library'], false, 'Create private library →');
  }
}

async function openLibraryByPin(event) {
  event.preventDefault();
  const pin = elements['library-pin-input'].value.replace(/\D/g, '');
  if (pin.length !== 6) return notice('Enter the six-digit library PIN.');
  setBusy(elements['open-library'], true, 'Opening…');
  try {
    const result = await apiRequest('libraries/access', { body: { pin } });
    state.token = result.accessToken;
    state.pin = pin;
    state.role = 'uploader';
    state.libraryId = result.libraryId;
    localStorage.setItem(STORAGE_KEY, state.token);
    localStorage.setItem(PIN_STORAGE_KEY, state.pin);
    elements['library-pin-input'].value = '';
    showDashboard();
    await loadCaches({ quiet: true });
    notice('Library opened. New GPX files will be added to its existing caches.');
  } catch (error) {
    notice(error.message || 'The library could not be opened.');
  } finally {
    setBusy(elements['open-library'], false, 'Open library →');
  }
}

async function loadCaches({ quiet = false } = {}) {
  if (!state.token) return;
  setBusy(elements['sync-list'], true, 'Refreshing…');
  try {
    const result = await apiRequest('caches', { token: state.token });
    state.caches = result.caches || [];
    renderCaches();
    if (!quiet) notice('Library refreshed.');
  } catch (error) {
    if (error.status === 401) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(PIN_STORAGE_KEY);
      state.token = '';
      state.pin = '';
      showWelcome();
      notice('This companion key is no longer valid.');
    } else {
      notice(error.message || 'Could not refresh the library.');
    }
  } finally {
    setBusy(elements['sync-list'], false, 'Refresh');
  }
}

async function handleFiles(event) {
  const files = Array.from(event.target.files || []);
  if (!files.length) return;
  if (files.length > 20) {
    notice('Choose up to 20 GPX files at once.');
    event.target.value = '';
    return;
  }
  if (files.some((file) => file.size > 4_000_000)) {
    notice('Each GPX must be smaller than 4 MB.');
    event.target.value = '';
    return;
  }
  try {
    const lists = [];
    for (const file of files) lists.push(parseGpx(await file.text()));
    state.pendingCaches = combineCacheLists(lists);
    if (state.pendingCaches.length > 1000) throw new Error('This GPX contains more than 1,000 caches.');
    state.pendingName = files.length === 1
      ? files[0].name
      : `${files.length} GPX files · ${files.slice(0, 3).map((file) => file.name).join(', ')}${files.length > 3 ? '…' : ''}`;
    renderImportPreview();
  } catch (error) {
    state.pendingCaches = [];
    elements['import-preview'].classList.add('hidden');
    notice(error.message || 'The GPX could not be read.');
  }
}

function useSampleCaches() {
  state.pendingCaches = SAMPLE_CACHES.map((cache) => ({ ...cache }));
  state.pendingName = 'Geo Cache Navigator demo data';
  renderImportPreview();
  notice('Demo caches prepared. Choose Add to library to save them.');
}

function renderImportPreview() {
  elements['preview-count'].textContent = `${state.pendingCaches.length} cache${state.pendingCaches.length === 1 ? '' : 's'}`;
  elements['preview-name'].textContent = state.pendingName;
  elements['import-preview'].classList.remove('hidden');
}

async function importPending() {
  if (!state.pendingCaches.length) return;
  setBusy(elements['import-button'], true, 'Importing…');
  try {
    const result = await apiRequest('caches/import', { token: state.token, body: { caches: state.pendingCaches } });
    state.pendingCaches = [];
    elements['import-preview'].classList.add('hidden');
    elements['gpx-file'].value = '';
    await loadCaches({ quiet: true });
    notice(`${result.added} added · ${result.updated} updated · ${result.total} total.`);
  } catch (error) {
    notice(error.message || 'The GPX import failed.');
  } finally {
    setBusy(elements['import-button'], false, 'Add to library');
  }
}

async function createPairing() {
  if (state.role !== 'admin') return;
  const warning = state.pin
    ? 'Reset the permanent PIN? Existing glasses and PIN-based upload sessions will be disconnected.'
    : 'Create a permanent PIN for this library?';
  if (!window.confirm(warning)) return;
  setBusy(elements['pair-button'], true, state.pin ? 'Resetting…' : 'Creating…');
  try {
    const result = await apiRequest('pins/rotate', { token: state.token, body: {} });
    state.pin = result.pin;
    localStorage.setItem(PIN_STORAGE_KEY, state.pin);
    renderPin();
    notice(`Permanent library PIN set to ${formatPin(state.pin)}.`);
  } catch (error) {
    notice(error.message || 'The library PIN could not be changed.');
  } finally {
    setBusy(elements['pair-button'], false, state.pin ? 'Reset permanent PIN' : 'Create permanent PIN');
  }
}

function renderPin() {
  const hasPin = state.pin.length === 6;
  elements['pair-placeholder'].classList.toggle('hidden', hasPin);
  elements['pair-code'].classList.toggle('hidden', !hasPin);
  if (hasPin) elements['pair-code'].querySelector('strong').textContent = formatPin(state.pin);
  elements['pair-expiry'].textContent = 'Does not expire · use on phone or glasses';
  elements['pair-button'].textContent = hasPin ? 'Reset permanent PIN' : 'Create permanent PIN';
}

function renderAccessControls() {
  const isAdmin = state.role === 'admin';
  elements['pair-button'].classList.toggle('hidden', !isAdmin);
  elements['clear-button'].classList.toggle('hidden', !isAdmin);
  elements['recovery-panel'].classList.toggle('hidden', !isAdmin);
}

function formatPin(pin) {
  return `${pin.slice(0, 3)} ${pin.slice(3)}`;
}

async function clearCaches() {
  if (!state.caches.length) return;
  if (!window.confirm('Remove all imported GPX caches from this private library?')) return;
  try {
    await apiRequest('caches/clear', { token: state.token, method: 'DELETE' });
    state.caches = [];
    renderCaches();
    notice('Imported caches cleared.');
  } catch (error) {
    notice(error.message || 'The library could not be cleared.');
  }
}

function renderCaches() {
  elements['library-summary'].textContent = state.caches.length
    ? `${state.caches.length} private cache${state.caches.length === 1 ? '' : 's'} ready to sync.`
    : 'No caches imported yet.';
  if (!state.caches.length) {
    elements['cache-list'].innerHTML = '<p class="empty-message">Import a GPX to start.</p>';
    return;
  }
  const fragment = document.createDocumentFragment();
  state.caches.forEach((cache) => {
    const row = document.createElement('div');
    row.className = 'cache-row';
    const copy = document.createElement('div');
    const name = document.createElement('strong');
    name.textContent = cache.name;
    const code = document.createElement('small');
    code.textContent = cache.externalCode;
    copy.append(name, code);
    const type = document.createElement('span');
    type.className = 'cache-type';
    type.textContent = cache.type || 'cache';
    const rating = document.createElement('span');
    rating.textContent = cache.difficulty && cache.terrain ? `D ${cache.difficulty} · T ${cache.terrain}` : 'GPX';
    row.append(copy, type, rating);
    fragment.append(row);
  });
  elements['cache-list'].replaceChildren(fragment);
}

async function copyRecoveryKey() {
  try {
    await navigator.clipboard.writeText(state.token);
    notice('Recovery key copied. Treat it like a password.');
  } catch {
    window.prompt('Copy this recovery key and keep it private:', state.token);
  }
}

function forgetLibrary() {
  if (!window.confirm('Forget this library on this browser? The cloud data will remain. The PIN can reopen uploads; the recovery key is required for full management.')) return;
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(PIN_STORAGE_KEY);
  state.token = '';
  state.pin = '';
  state.role = '';
  state.caches = [];
  showWelcome();
  notice('Library forgotten on this browser.');
}

function switchLibrary() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(PIN_STORAGE_KEY);
  state.token = '';
  state.pin = '';
  state.role = '';
  state.caches = [];
  showWelcome();
  notice('Enter a library PIN or create a new library.');
}

async function deleteLibrary() {
  if (!window.confirm('Permanently delete this cloud library, its imported caches, PIN access, and connected devices?')) return;
  try {
    await apiRequest('libraries', { token: state.token, method: 'DELETE' });
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(PIN_STORAGE_KEY);
    state.token = '';
    state.pin = '';
    state.role = '';
    state.caches = [];
    showWelcome();
    notice('Cloud library permanently deleted.');
  } catch (error) {
    notice(error.message || 'The cloud library could not be deleted.');
  }
}

function setBusy(button, busy, label) {
  button.disabled = busy;
  button.textContent = label;
}

function notice(message) {
  clearTimeout(state.noticeTimer);
  elements.notice.textContent = message;
  elements.notice.classList.remove('hidden');
  state.noticeTimer = setTimeout(() => elements.notice.classList.add('hidden'), 4800);
}
